import { array, nonEmptyArray, option, record, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';

import { TimeUnit } from '@mews/enums';
import { getBooleanProp, getProp, getStringProp, keyBy, values } from '@mews/fp';

import { DateTimeService, DateTimeUtc, DateTimeUtcService, DateTimeUtils } from '@mews/framework';
import { parentSelector } from '../../selectors/parentSelector';
import { Availability, Category } from '../../types/data';
import { toDateTimeUtc } from '../../utils/dates';
import { getAvailabilityHash } from '../../utils/getAvailabilityHash';
import { AvailableCategories, getAvailableCategories } from '../availableCategories';
import { getEnterprises, getEnterpriseTimezone } from '../entities/enterprises/selectors';
import {
    getBookableServices,
    getDayUseServiceEnterpriseIds,
    hasEnterpriseDayUseServices,
    isServiceHourly,
} from '../entities/services/selectors';
import { BookableServices } from '../entities/services/types';
import { getEndDate, getEndTimeUtc, getStartDate, getStartTimeUtc, getVoucherCode } from '../resourceOrder/selectors';
import { createCategoriesAvailability } from './createCategoriesAvailability';
import {
    AvailableStartTimes,
    CategoryAvailability,
    ServiceAvailabilityData,
    TimeUnitsCategoryAvailability,
} from './types';

const getServiceAvailabilitySlice = flow(parentSelector, (state) => state.data.serviceAvailability);

export const getAllServiceAvailabilityMeta = flow(
    getServiceAvailabilitySlice,
    (serviceAvailability) => serviceAvailability.meta
);

export const isAnyServiceAvailabilityFetching = flow(
    getAllServiceAvailabilityMeta,
    record.some(({ isFetching }) => isFetching)
);

export const getServiceAvailabilityData = flow(
    getServiceAvailabilitySlice,
    (serviceAvailability) => serviceAvailability.data
);

export const getAvailableDateTimes = (serviceAvailability: ServiceAvailabilityData) => {
    const startTimesByCategoryId = values(serviceAvailability).reduce(
        (acc: Record<string, TimeUnitsCategoryAvailability>, { categoryId, timeUnitsAvailability }) => ({
            ...acc,
            [categoryId]: timeUnitsAvailability,
        }),
        {}
    );
    return values(startTimesByCategoryId).length > 0 ? startTimesByCategoryId : undefined;
};

export const getAvailableStartTimes = (serviceAvailability: ServiceAvailabilityData) => {
    const startTimesByCategoryId = values(serviceAvailability).reduce(
        (acc: Record<string, AvailableStartTimes>, { categoryId, availableStartTimes }) =>
            availableStartTimes ? { ...acc, [categoryId]: availableStartTimes } : acc,
        {}
    );
    return values(startTimesByCategoryId).length > 0 ? startTimesByCategoryId : undefined;
};

export const getAvailableDateTimesByCategoryId = flow(getServiceAvailabilityData, getAvailableDateTimes);
export const getAvailableStartTimesByCategoryId = flow(getServiceAvailabilityData, getAvailableStartTimes);

export const getServiceAvailabilityGroupedByEnterpriseId = createSelector(
    getServiceAvailabilityData,
    flow(values, (availabilities) =>
        array.isNonEmpty(availabilities)
            ? pipe(
                  availabilities,
                  nonEmptyArray.groupBy(({ enterpriseId }) => enterpriseId)
              )
            : {}
    )
);

export const getAvailabilityCategoryIds = createSelector(
    getServiceAvailabilityData,
    flow(
        values,
        array.map((availability) => availability.categoryId)
    )
);

export const getEnterpriseAvailabilityCategoryIds = createSelector(
    getStringProp('enterpriseId'),
    getServiceAvailabilityData,
    (enterpriseId, serviceAvailability) =>
        pipe(
            serviceAvailability,
            values,
            array.filterMap((availability) =>
                availability.enterpriseId === enterpriseId ? option.some(availability.categoryId) : option.none
            )
        )
);

interface DividedDatesAccumulator {
    available: Set<string>;
    occupied: Set<string>;
}

const getOccupiedAndAvailableDates = (
    { occupied, available }: DividedDatesAccumulator,
    timeUnitsAvailability: TimeUnitsCategoryAvailability
) =>
    pipe(
        timeUnitsAvailability,
        record.toArray,
        array.reduce(
            { occupied: new Set(occupied), available: new Set(available) },
            (acc, [currentDate, occupancy]) => {
                if (occupancy === 0) {
                    acc.occupied.add(currentDate);
                }
                if (occupancy > 0) {
                    acc.available.add(currentDate);
                }
                return acc;
            }
        )
    );

const filterOccupiedDates = ({ occupied, available }: DividedDatesAccumulator) =>
    new Set([...occupied].filter((date) => !available.has(date)));

const getEnterprisesCategoryAvailabilities = createSelector(
    getServiceAvailabilityData,
    getProp<string[]>()('enterpriseIds'),
    (availableCategories, enterpriseIds) =>
        pipe(
            availableCategories,
            values,
            array.filter(({ enterpriseId }) => enterpriseIds.includes(enterpriseId))
        )
);

const getOccupiedCategoryAvailabilities = createSelector(
    getEnterprisesCategoryAvailabilities,
    (categoryAvailabilities) =>
        pipe(
            categoryAvailabilities,
            array.map(({ timeUnitsAvailability }) =>
                pipe(
                    timeUnitsAvailability,
                    record.filter((value) => value === 0),
                    record.keys
                )
            )
        )
);

const getFilteredDates = createSelector(getEnterprisesCategoryAvailabilities, (enterprisesCategories) =>
    pipe(
        enterprisesCategories,
        array.reduce<CategoryAvailability, DividedDatesAccumulator>(
            { available: new Set(), occupied: new Set() },
            (acc, { timeUnitsAvailability }) => getOccupiedAndAvailableDates(acc, timeUnitsAvailability)
        )
    )
);

export const getOccupiedStartDates = createSelector(getFilteredDates, filterOccupiedDates);

export const getMaxAvailableEndDate = createSelector(
    getOccupiedCategoryAvailabilities,
    getStringProp('selectedStartDateUtc'),
    (categoryOccupiedAvailabilities, selectedStartDateUtc) =>
        pipe(
            categoryOccupiedAvailabilities,
            array.filter((occupiedDates) => !occupiedDates.includes(selectedStartDateUtc)),
            array.map(
                flow(
                    array.filter((occupiedDateUtc) => occupiedDateUtc > selectedStartDateUtc),
                    array.sort(string.Ord),
                    array.head
                )
            ),
            array.sequence(option.Applicative),
            option.flatMap(flow(array.sort(string.Ord), array.last)),
            option.toNullable
        )
);

const getTimeUnitFromBookableServices = (bookableServices: BookableServices, enterpriseId: string) =>
    pipe(
        bookableServices,
        values,
        array.findFirst((service) => service.enterpriseId === enterpriseId),
        option.map((enterpriseService) => enterpriseService.data.value.timeUnit),
        option.getOrElse(() => TimeUnit.Day)
    );

export const hasIntervalAvailableCategories = createSelector(
    getServiceAvailabilityGroupedByEnterpriseId,
    /* 2021-01-01 */
    getStringProp('startDate'),
    /* 2021-01-01 */
    getStringProp('endDate'),
    getEnterpriseTimezone,
    hasEnterpriseDayUseServices,
    getStringProp('enterpriseId'),
    getBookableServices,
    getProp<string | undefined>()('startTimeUtc'),
    getProp<string | undefined>()('endTimeUtc'),
    getBooleanProp('isOvernightBookingEnabled'),
    (
        serviceAvailability,
        startDate,
        endDate,
        timezone,
        isDayUse,
        enterpriseId,
        bookableServices,
        startTimeUtc,
        endTimeUtc,
        isOvernightBookingEnabled
    ) => {
        const serviceCategoriesAvailability = serviceAvailability?.[enterpriseId];
        if (serviceCategoriesAvailability == null || timezone == null) {
            return false;
        }

        const timeUnit = getTimeUnitFromBookableServices(bookableServices, enterpriseId);

        const availableCategories = createCategoriesAvailability({
            startDate,
            endDate,
            serviceCategoriesAvailability,
            isDayUse,
            timezone,
            timeUnit,
            startTimeUtc,
            endTimeUtc,
            isHourlyService: startTimeUtc != null && endTimeUtc != null,
            isOvernightBookingEnabled,
        });

        return pipe(
            availableCategories,
            record.toArray,
            array.some(([_reservationGroupId, { availableCount }]) => availableCount > 0)
        );
    }
);

export const checkIsOvernightBookingEnabled = (serviceAvailability: CategoryAvailability[], timezone: string) => {
    const availableStartTimesPresent = serviceAvailability.some(
        ({ availableStartTimes }) => availableStartTimes != null
    );
    const dataHasUtcDatesByHour = serviceAvailability.some(({ timeUnitsAvailability }) => {
        const dateSet = new Set();
        for (const date of Object.keys(timeUnitsAvailability)) {
            const calendarDate = DateTimeUtils.dateTimeUtcStringToWallClockDate(date, timezone).value;
            if (dateSet.has(calendarDate)) {
                return true;
            }
            dateSet.add(calendarDate);
        }
        return false;
    });
    return !availableStartTimesPresent && dataHasUtcDatesByHour;
};

export const getServiceAvailabilitiesInLegacyFormat = createSelector(
    getEndDate,
    getEnterprises,
    getServiceAvailabilityGroupedByEnterpriseId,
    getStartDate,
    getVoucherCode,
    getAvailableCategories,
    getDayUseServiceEnterpriseIds,
    getBookableServices,
    getStartTimeUtc,
    getEndTimeUtc,
    (
        endDate,
        enterprises,
        serviceAvailability,
        startDate,
        voucherCode,
        availableCategories,
        dayUseServiceEnterpriseIds,
        bookableServices,
        startTimeUtc,
        endTimeUtc
    ) => {
        if (startDate == null || endDate == null) {
            return {};
        }
        const isHourlyService = startTimeUtc != null && endTimeUtc != null;
        return pipe(
            serviceAvailability,
            record.toArray,
            array.reduce<[string, CategoryAvailability[]], Availability>(
                {},
                (acc, [enterpriseId, serviceCategoriesAvailability]) => {
                    const enterpriseTimezone = enterprises[enterpriseId]?.ianaTimeZoneIdentifier;

                    if (enterpriseTimezone == null) {
                        return acc;
                    }

                    const isOvernightBookingEnabled =
                        isHourlyService &&
                        checkIsOvernightBookingEnabled(serviceCategoriesAvailability, enterpriseTimezone);
                    const timeUnit = getTimeUnitFromBookableServices(bookableServices, enterpriseId);

                    const updateLegacyAvailabilitiesAccumulator = createLegacyFormatAvailabilities({
                        acc,
                        availableCategories,
                        enterpriseId,
                        enterpriseTimezone,
                        serviceCategoriesAvailability,
                        voucherCode,
                        isDayUse: dayUseServiceEnterpriseIds.includes(enterpriseId),
                        timeUnit,
                        isOvernightBookingEnabled,
                    });

                    const { suggestions } = getSelectedAvailabilityRestrictionData({
                        startDate,
                        endDate,
                        availableCategories,
                        enterpriseId,
                        voucherCode,
                        startTimeUtc,
                        endTimeUtc,
                    });

                    updateLegacyAvailabilitiesAccumulator({
                        startDate,
                        endDate,
                        startTimeUtc,
                        endTimeUtc,
                        isHourlyService,
                    });

                    if (isHourlyService) {
                        return acc;
                    }

                    // if suggestions are present, create availability for each suggestion.
                    suggestions?.forEach(({ startDate: suggestionStartDate, endDate: suggestionEndDate }) => {
                        updateLegacyAvailabilitiesAccumulator({
                            startDate: suggestionStartDate,
                            endDate: suggestionEndDate,
                            startTimeUtc,
                            endTimeUtc,
                            // we dont pass isHourlyService here because we want to check daily availability for suggestions
                        });
                    });
                    return acc;
                }
            )
        );
    }
);

interface CreateLegacyFormatAvailabilityOptions {
    acc: Availability;
    availableCategories: AvailableCategories;
    enterpriseId: string;
    enterpriseTimezone: string;
    serviceCategoriesAvailability: CategoryAvailability[];
    voucherCode: string | undefined;
    isDayUse: boolean;
    timeUnit: TimeUnit;
    isOvernightBookingEnabled: boolean;
}

interface CreateLegacyFormatAvailabilityArgs {
    startDate: string;
    endDate: string;
    startTimeUtc?: string;
    endTimeUtc?: string;
    isHourlyService?: boolean;
}

const createLegacyFormatAvailabilities =
    ({
        acc,
        availableCategories,
        enterpriseId,
        enterpriseTimezone: timezone,
        serviceCategoriesAvailability,
        voucherCode,
        isDayUse,
        timeUnit,
        isOvernightBookingEnabled,
    }: CreateLegacyFormatAvailabilityOptions) =>
    ({ startDate, endDate, startTimeUtc, endTimeUtc, isHourlyService }: CreateLegacyFormatAvailabilityArgs) => {
        const { legacyHashId, violatedRestrictionIds, availableCategoryIds, suggestions } =
            getSelectedAvailabilityRestrictionData({
                startDate,
                endDate,
                voucherCode,
                availableCategories,
                enterpriseId,
                startTimeUtc,
                endTimeUtc,
            });

        const unrestrictedCategories = createCategoriesAvailability({
            serviceCategoriesAvailability,
            startDate,
            endDate,
            isDayUse,
            timezone,
            timeUnit,
            startTimeUtc,
            endTimeUtc,
            isHourlyService,
            isOvernightBookingEnabled,
        });

        const categories = pipe(
            availableCategoryIds,
            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{} | EnforceKeyabl...
            array.map((categoryId: string) => option.fromNullable(unrestrictedCategories[categoryId])),
            array.compact,
            keyBy<Category>('id')
        );

        acc[legacyHashId] = {
            enterpriseId,
            startDate,
            endDate,
            startTimeUtc,
            endTimeUtc,
            categories,
            violatedRestrictionIds,
            hasFailed: false,
            isFetching: false,
            suggestions,
        };
    };
interface GetSelectedAvailabilityDataOptions {
    startDate: string;
    endDate: string;
    enterpriseId: string;
    voucherCode: string | undefined;
    availableCategories: AvailableCategories;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

const getSelectedAvailabilityRestrictionData = ({
    availableCategories,
    startDate,
    endDate,
    enterpriseId,
    voucherCode,
    startTimeUtc,
    endTimeUtc,
}: GetSelectedAvailabilityDataOptions) => {
    const legacyHashId = getAvailabilityHash({
        startDate: startTimeUtc ?? startDate,
        endDate: endTimeUtc ?? endDate,
        enterpriseId,
        voucherCode,
    });

    const { violatedRestrictionIds, availableCategoryIds, suggestions } = availableCategories[legacyHashId] ?? {
        availableCategoryIds: [],
        violatedRestrictionIds: [],
        suggestions: [],
    };

    return { legacyHashId, violatedRestrictionIds, availableCategoryIds, suggestions };
};

const initialMonthsFetchInterval = 2;
const hourlyInitialMonthsFetchInterval = 1;

const dateStringToUtc = (dateTimeUtc: DateTimeUtc, timezone: string): DateTimeUtc =>
    pipe(
        dateTimeUtc,
        (date) => DateTimeService.fromUtc(date, timezone),
        (date) => DateTimeService.startOfDay(date),
        (date) => DateTimeService.toUtc(date)
    );

/**
 * Returns interval calculated from startDate ( respecting deeplink & widgetAPI )
 * Interval is calculated
 * start = startDate - 1 month,
 * end = startDate + 2 months
 *
 */
export const getInitialAvailabilityInterval = createSelector(
    getStartDate,
    getEndDate,
    getProp<DateTimeUtc>()('nowUtc'),
    getEnterpriseTimezone,
    isServiceHourly,
    (wallClockStartDateString, wallClockEndDateString, nowUtc, enterpriseTimezone, isHourlyService) => {
        const monthInterval = isHourlyService ? hourlyInitialMonthsFetchInterval : initialMonthsFetchInterval;
        if (enterpriseTimezone == null) {
            return { startUtc: nowUtc, endUtc: DateTimeUtcService.addMonths(nowUtc, monthInterval) };
        }

        const startDateUtc =
            wallClockStartDateString != null ? toDateTimeUtc(wallClockStartDateString, enterpriseTimezone) : nowUtc;
        const endDateUtc =
            wallClockEndDateString != null ? toDateTimeUtc(wallClockEndDateString, enterpriseTimezone) : null;
        let finalStartDateUtc = DateTimeUtcService.subtractMonths(startDateUtc, 1);
        if (DateTimeUtcService.isLess(finalStartDateUtc, nowUtc)) {
            finalStartDateUtc = nowUtc;
        }

        let finalEndUtc = DateTimeUtcService.addMonths(startDateUtc, monthInterval);

        if (
            endDateUtc != null &&
            DateTimeUtcService.isGreater(endDateUtc, finalEndUtc) &&
            DateTimeUtcService.diffMonths(endDateUtc, finalStartDateUtc) <= 11
        ) {
            finalEndUtc = endDateUtc;
        }

        return {
            startUtc: dateStringToUtc(finalStartDateUtc, enterpriseTimezone),
            endUtc: dateStringToUtc(finalEndUtc, enterpriseTimezone),
        };
    }
);
